import React, { useState, useEffect, useRef } from 'react';
import { executiveFormationService } from '../services/executiveFormationService';
import { supabase } from '../supabaseClient';
import LogoCarousel from './LogoCarousel';
import { HiCheckCircle, HiAcademicCap, HiSparkles, HiMail, HiUser, HiPhone, HiBadgeCheck, HiArrowDown } from 'react-icons/hi';
import './ExecutiveFormations.css';

function ExecutiveFormations({ edition: customEdition }) {
  const [settings, setSettings] = useState({
    hero_title: 'FORMATIONS EXECUTIVE & CERTIFICATIONS',
    hero_subtitle: 'Développez vos compétences avec nos programmes d’excellence executive',
    intro_text: 'Nos formations executive combinent excellence académique, expertise terrain et certifications reconnues.',
    is_visible: true
  });
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    certificate_id: '',
    certificate_name: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [editionId, setEditionId] = useState(null);
  
  const formRef = useRef(null);

  useEffect(() => {
    loadData();
  }, [customEdition]);

  const loadData = async () => {
    try {
      setLoading(true);
      let year = customEdition;
      if (!year) {
        const { data: activeEdition } = await supabase
          .from('editions')
          .select('id, year')
          .eq('is_active', true)
          .single();
        year = activeEdition?.year || '2025';
        if (activeEdition?.id) setEditionId(activeEdition.id);
      } else {
        const { data: ed } = await supabase
          .from('editions')
          .select('id')
          .eq('year', year)
          .single();
        if (ed?.id) setEditionId(ed.id);
      }

      const settingsData = await executiveFormationService.getSettings(year);
      setSettings(settingsData);

      const certsData = await executiveFormationService.getCertificates(year);
      const activeCerts = certsData.filter(c => c.is_active !== false);
      setCertificates(activeCerts);
      
      if (activeCerts.length > 0) {
        setFormData(prev => ({
          ...prev,
          certificate_id: activeCerts[0].id,
          certificate_name: activeCerts[0].title
        }));
      }
    } catch (err) {
      console.error('Error loading executive formations page:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'certificate_id') {
      const selectedCert = certificates.find(c => String(c.id) === String(value));
      setFormData({
        ...formData,
        certificate_id: selectedCert ? selectedCert.id : value,
        certificate_name: selectedCert ? selectedCert.title : value
      });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const scrollToForm = () => {
    if (formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleSelectCertificate = (cert) => {
    setFormData(prev => ({
      ...prev,
      certificate_id: cert.id,
      certificate_name: cert.title
    }));
    scrollToForm();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.email || !formData.nom || !formData.prenom || !formData.telephone) {
      alert('Veuillez remplir tous les champs obligatoires.');
      return;
    }

    setSubmitting(true);
    try {
      let finalEditionId = editionId;
      if (!finalEditionId) {
        const { data: ed } = await supabase.from('editions').select('id').eq('is_active', true).single();
        finalEditionId = ed?.id || 1;
      }

      const certificateId = Number(formData.certificate_id);
      await executiveFormationService.registerExecutive({
        edition_id: finalEditionId,
        certificate_id: Number.isFinite(certificateId) ? certificateId : null,
        certificate_name: formData.certificate_name || 'Executive Program',
        nom: formData.nom,
        prenom: formData.prenom,
        email: formData.email,
        telephone: formData.telephone,
        status: 'en_attente'
      });

      setSuccess(true);
      setFormData({
        nom: '',
        prenom: '',
        email: '',
        telephone: '',
        certificate_id: certificates.length > 0 ? certificates[0].id : '',
        certificate_name: certificates.length > 0 ? certificates[0].title : ''
      });
    } catch (err) {
      console.error(err);
      alert('Une erreur est survenue lors de l\'enregistrement de votre demande. Veuillez réessayer.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="exec-loading">Chargement des programmes executive...</div>;
  }

  if (settings && settings.is_visible === false) {
    return null;
  }

  return (
    <div className="executive-formations-page">
      {/* Top Banner / Hero Section */}
      <div className="exec-hero">
        <div className="exec-hero-overlay"></div>
        <div className="exec-hero-content">
          
          <h1 className="exec-title">{settings.hero_title || 'Formations Executive & Certifications'}</h1>
          <p className="exec-subtitle">
            {settings.hero_subtitle || 'Renforcez votre profil professionnel avec nos parcours de formation certifiants et hautement qualifiants.'}
          </p>
          <p className="exec-intro">{settings.intro_text}</p>
          
          <div className="exec-hero-actions">
            <button className="btn-hero-inscription" onClick={scrollToForm}>
              Inscriptions Ouvertes <HiArrowDown />
            </button>
          </div>
        </div>
      </div>

      {/* Program Highlights & Certificates Grid */}
      <div className="exec-container">
        <div className="section-header text-center">
          <h2 className="section-title">Programmes & Certificats Executive</h2>
         
          <p className="section-description">
            Nos programmes sont conçus pour les cadres, dirigeants, chercheurs et professionnels désireux d'acquérir une expertise opérationnelle de haut niveau.
          </p>
        </div>

        <div className="certificates-grid">
          {certificates.map((cert) => (
            <div key={cert.id} className="certificate-card">
              {/* <div className="cert-card-header">
                <div className="cert-icon-wrapper">
                  <HiAcademicCap size={32} />
                </div>
                {cert.duration && <span className="cert-duration">{cert.duration}</span>}
              </div> */}
              <h3 className="cert-title">{cert.title}</h3>
              <p className="cert-desc">{cert.description}</p>
              
              <ul className="cert-benefits">
                <li><HiCheckCircle className="check-icon" /> Intervenants et experts internationaux</li>
                <li><HiCheckCircle className="check-icon" /> Horaires adaptés aux professionnels</li>
                <li><HiCheckCircle className="check-icon" /> Attestation & Certificat de réussite</li>
              </ul>
              
              <button className="btn-select-cert" onClick={() => handleSelectCertificate(cert)}>
                Sélectionner ce certificat
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Partnership Logos Section */}
      {/* <div className="exec-partners-section">
        <div className="exec-container">
          <div className="partners-header text-center">
            <span className="partners-tag">Excellence Académique & Institutionnelle</span>
            <h3>Nos Partenaires de Formation Executive</h3>
            <p>Formations délivrées en partenariat avec des institutions universitaires et scientifiques de premier plan.</p>
          </div>
          <div className="partners-carousel-wrapper">
            <LogoCarousel />
          </div>
        </div>
      </div> */}

      {/* Inscription Form Section */}
      <div className="exec-registration-section" ref={formRef}>
        <div className="exec-container">
          <div className="form-card-wrapper">
            <div className="form-info-side">
             
              <h2>Inscrivez-vous dès maintenant</h2>
              <p>
                Complétez le formulaire ci-contre pour réserver votre place. Notre équipe pédagogique vous recontactera rapidement avec l'ensemble des détails du programme.
              </p>
              
              <div className="info-bullets">
                <div className="info-item">
                  <span className="bullet-num">1</span>
                  <div>
                    <h4>Choix du certificat</h4>
                    <p>Sélectionnez le programme qui correspond le mieux à vos objectifs professionnels.</p>
                  </div>
                </div>
                <div className="info-item">
                  <span className="bullet-num">2</span>
                  <div>
                    <h4>Confirmation du dossier</h4>
                    <p>Recevez votre dossier d'admission ainsi que les modalités administratives.</p>
                  </div>
                </div>
                <div className="info-item">
                  <span className="bullet-num">3</span>
                  <div>
                    <h4>Accès aux sessions</h4>
                    <p>Rejoignez la promotion et accédez à notre plateforme d'apprentissage.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="form-box-side">
              {success ? (
                <div className="form-success-box">
                  <div className="success-icon-big">✓</div>
                  <h3>Inscription envoyée avec succès !</h3>
                  <p>
                    Merci pour votre intérêt. Votre demande de candidature a bien été prise en compte.
                  </p>
                  <p className="success-subtext">Un responsable d'admission prendra contact avec vous rapidement par e-mail ou téléphone.</p>
                  <button className="btn-reset" onClick={() => setSuccess(false)}>
                    Faire une autre inscription
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="exec-form">
                  <h3 className="form-title">Formulaire d'inscription aux Formations</h3>
                  
                  <div className="form-group">
                    <label htmlFor="email">Adresse E-mail *</label>
                    <input 
                      type="email" 
                      id="email" 
                      name="email" 
                      required 
                      placeholder="exemple@domaine.com"
                      value={formData.email} 
                      onChange={handleInputChange} 
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="nom">Nom *</label>
                      <input 
                        type="text" 
                        id="nom" 
                        name="nom" 
                        required 
                        placeholder="Votre nom"
                        value={formData.nom} 
                        onChange={handleInputChange} 
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="prenom">Prénom *</label>
                      <input 
                        type="text" 
                        id="prenom" 
                        name="prenom" 
                        required 
                        placeholder="Votre prénom"
                        value={formData.prenom} 
                        onChange={handleInputChange} 
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="telephone">Numéro de Téléphone *</label>
                    <input 
                      type="tel" 
                      id="telephone" 
                      name="telephone" 
                      required 
                      placeholder="+212 600000000"
                      value={formData.telephone} 
                      onChange={handleInputChange} 
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="certificate_id">Sélectionner le Certificat *</label>
                    <select 
                      id="certificate_id" 
                      name="certificate_id" 
                      required 
                      value={formData.certificate_id} 
                      onChange={handleInputChange}
                    >
                      {certificates.map((cert) => (
                        <option key={cert.id} value={cert.id}>
                          {cert.title}
                        </option>
                      ))}
                    </select>
                  </div>

                  <button type="submit" className="btn-submit-exec" disabled={submitting}>
                    {submitting ? 'Traitement de votre inscription...' : 'VALIDER MON INSCRIPTION'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ExecutiveFormations;
